import { useEffect, useState, useRef } from 'react';
import logger from '~/services/logger';

interface CleengTokens {
  jwt: string | null;
  refreshToken: string | null;
}

declare global {
  interface Window {
    cleeng?: {
      onAuthTokensUpdate: (callback: () => void) => void;
      logout: () => void;
      setAuthTokens: (tokens: CleengTokens) => void;
      onWidgetEvent: (
        callback: (event: { name: string; data: any }) => void
      ) => void;
    };
  }
}

interface UseCleengLowCodeProps {
  cleeng_environment: string;
  authData?: {
    access_token?: string;
    refresh_token?: string;
  };
  onWidgetEvent?: (event: { name: string; data: any }) => void;
}

interface UseCleengLowCodeReturn {
  isCleengLowCodeLoading: boolean;
  authTokensLoaded: boolean;
}

const CLEENG_PROD_URL = 'https://widgets.prod.cleeng.com/cleeng.js';
const CLEENG_SANDBOX_URL = 'https://widgets.sandbox.cleeng.com/cleeng.js';

export function useCleengLowCode({
  cleeng_environment,
  authData,
  onWidgetEvent,
}: UseCleengLowCodeProps): UseCleengLowCodeReturn {
  const [isCleengLowCodeLoading, setIsCleengLowCodeLoading] = useState(true);
  const [authTokensLoaded, setAuthTokensLoaded] = useState(false);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    // Load Cleeng widget script
    const script = document.createElement('script');
    script.type = 'module';
    script.src =
      cleeng_environment === 'production'
        ? CLEENG_PROD_URL
        : CLEENG_SANDBOX_URL;

    script.onload = () => {
      setIsCleengLowCodeLoading(false);
    };

    script.onerror = () => {
      setIsCleengLowCodeLoading(false);
    };

    document.head.appendChild(script);
    scriptRef.current = script;

    // Handle Cleeng widget messages
    const handleCleengMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CLEENG_SDK_LOADED') {
        if (window.cleeng) {
          window.cleeng.onAuthTokensUpdate(() => {
            setAuthTokensLoaded(true);
          });

          if (onWidgetEvent) {
            window.cleeng.onWidgetEvent(onWidgetEvent);
          }
        } else {
          logger.error(
            `useCleengLowCode: window.cleeng is not available after SDK load (event type: ${event.data?.type})`
          );
        }
      }
    };

    window.addEventListener('message', handleCleengMessage);

    // Cleanup function
    return () => {
      if (scriptRef.current && document.head.contains(scriptRef.current)) {
        document.head.removeChild(scriptRef.current);
      }
      scriptRef.current = null;
      window.removeEventListener('message', handleCleengMessage);
    };
  }, [cleeng_environment]);

  // Combined effect for auth token management and logout
  useEffect(() => {
    if (authTokensLoaded && authData?.access_token && authData?.refresh_token) {
      window.cleeng?.setAuthTokens({
        jwt: authData.access_token,
        refreshToken: authData.refresh_token,
      });
    }

    // Logout from Cleeng widget when user logs out from main app
    if (!authData?.access_token && window.cleeng) {
      window.cleeng.logout();
    }
  }, [authTokensLoaded, authData?.access_token, authData?.refresh_token]);

  return {
    isCleengLowCodeLoading,
    authTokensLoaded,
  };
}
